import { computed } from 'vue'
import { createRouter, createWebHistory, isNavigationFailure, NavigationFailureType, RouteRecordRaw } from 'vue-router'
import { scrollTo, until } from './includes/helpers'
import { isModalToggling } from './includes/modals'
import { useStore } from './stores/index'
import { load } from './classes/translations'
import { useUserStore } from './stores/user'
import Main from './components/main.vue'

// Dont use lazyload for these pages as they are used often
import SignInPage from './pages/title.vue'
import DashboardPage from './pages/dashboard.vue'

// Make a dummy component needed to comply with TS
const EmptyComponent = { template: '<div></div>' }

// Setting routes
const routes: RouteRecordRaw[] = [
	{
		path: '/',
		name: 'root',
		component: EmptyComponent,
		beforeEnter: navigateOnUnknownRoute
	},
	{
		path: '/:language',
		name: 'language',
		component: Main,
		children:
		[
			{
				path: '404',
				name: 'not-found',
				component: () => import('./pages/not-found.vue'),
				meta: {
					title: () => t.value.pageNotFoundTitle
				}
			},
			{
				path: 'invite/:referralId',
				redirect: 'dashboard',
			},
			{
				path: 'sign-in',
				name: 'sign-in',
				component: SignInPage,
				meta: { requiresNonAuth: true }
			},
			{
				path: 'phone-login',
				name: 'phone-login',
				component: () => import('./pages/phone-login.vue'),
				meta: {
					requiresNonAuth: true,
					title: () => t.value.phoneLoginTitle
				}
			},
			{
				path: 'registration',
				name: 'registration',
				component: () => import('./pages/registration.vue'),
				meta: {
					requiresNonAuth: true,
					title: () => t.value.registrationTitle
				}
			},
			{
				path: 'sms-code',
				name: 'sms-code',
				component: () => import('./pages/sms-code.vue'),
				meta: { requiresNonAuth: true }
			},
			{
				path: 'waitlist-invitation',
				name: 'waitlist-invitation',
				component: () => import('./pages/waitlist-invitation.vue'),
				meta: { requiresNonAuth: true }
			},
			{
				path: 'dashboard',
				name: 'dashboard',
				component: DashboardPage,
				meta: { requiresAuth: true }
			},
			{
				path: 'challenge/:challengeId',
				name: 'challenge',
				component: () => import('./pages/challenge.vue'),
				meta: {
					requiresAuth: true,
					hideMainMenuMobile: true,
				}
			},
			{
				path: 'history',
				name: 'history',
				component: () => import('./pages/history.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.historyTitle
				}
			},
			{
				path: 'prizes',
				name: 'prizes',
				component: () => import('./pages/prizes-categories.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.rewardsTitle
				}
			},
			{
				path: 'prizes/reward/:rewardId(\\d+)',
				name: 'prizes-reward',
				component: () => import('./pages/reward.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.rewardTitle,
					hideMainMenu: true,
				}
			},
			{
				path: 'prizes/rewards',
				name: 'prizes-rewards',
				component: () => import('./pages/rewards.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.myRewardsBlockTitle
				}
			},
			{
				path: 'prizes-list',
				name: 'prizes-list',
				component: () => import('./pages/prizes-list.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.rewardsTitle
				}
			},
			{
				path: 'points-transfer',
				name: 'points-transfer',
				component: () => import('./pages/points-transfer.vue'),
				meta: {
					requiresAuth: true,
				}
			},
			{
				path: 'prizes/:prizeId(\\d+)',
				name: 'prizes-category-prize',
				component: () => import('./pages/prize.vue'),
				meta: {
					requiresAuth: true,
				}
			},
			{
				path: 'prizes/:prizeId(\\d+)/loungekey',
				name: 'prizes-category-prize-loungekey',
				component: () => import('./pages/prize-loungekey-step.vue'),
				meta: {
					requiresAuth: true,
				}
			},
			{
				path: 'leaderboard',
				name: 'leaderboard',
				component: () => import('./pages/leaderboard.vue'),
				meta: {
					requiresAuth: true,
					title: () => t.value.leaderboardTitle
				}
			},
			{
				path: 'profile',
				component: () => import('./pages/profile.vue'),
				children: [
					{
						path: '',
						name: 'profile',
						component: () => import('./pages/profile/profile-menu.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'codes-menu',
						name: 'profile/profile-codes-menu',
						component: () => import('./pages/profile/profile-codes-menu.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'communication',
						name: 'profile/communication',
						component: () => import('./pages/profile/profile-communication.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'notification-types',
						name: 'profile/notification-types',
						component: () => import('./pages/profile/profile-notification-types.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'sounds',
						name: 'profile/sounds',
						component: () => import('./pages/profile/profile-sounds.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'social',
						name: 'profile/social',
						component: () => import('./pages/profile/profile-social.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'referral-code',
						name: 'profile/referral-code',
						component: () => import('./pages/profile/profile-referral-code.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'promo-code',
						name: 'profile/promo-code',
						component: () => import('./pages/profile/profile-promo-code.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'reset-snoozed-challenges',
						name: 'profile/reset-snoozed-challenges',
						component: () => import('./pages/profile/profile-reset-snoozed-challenges.vue'),
						meta: {
							requiresAuth: true,
						}
					},
					{
						path: 'deactivate-account',
						name: 'profile/deactivate-account',
						component: () => import('./pages/profile/profile-deactivate-account.vue'),
						meta: {
							requiresAuth: true,
						}
					}
				],
				meta: {
					requiresAuth: true,
					title: () => t.value.profileTitle
				}
			},
			{
				path: 'help',
				name: 'help',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'terms',
				name: 'terms',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'about',
				name: 'about',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'business',
				name: 'business',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'privacy-policy',
				name: 'privacy-policy',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'welcome',
				name: 'welcome',
				component: () => import('./pages/text.vue'),
			},
			{
				path: 'news',
				name: 'news',
				component: () => import('./pages/news.vue')
			},
			{
				path: 'news/:id(\\d+)',
				name: 'news-item',
				component: () => import('./pages/news-item.vue'),
			},
			{
				path: 'monthly-top-user/:id/:urlIdentifier',
				name: 'monthly-top-user',
				component: () => import('./pages/monthly-top-user.vue'),
				meta: {
					hideMainMenuMobile: true,
					hideMainMenuDesktop: true,
				}
			},
			{
				path: 'email-confirmation',
				name: 'email-confirmation',
				component: () => import('./pages/email-confirmation.vue'),
				meta: {
					hideMainMenuMobile: true
				}
			},
			{
				path: 'email-unsubscribe',
				name: 'email-unsubscribe',
				component: () => import('./pages/email-unsubscribe.vue'),
				meta: {
					hideMainMenuMobile: true
				}
			},
			{
				path: 'terms-and-conditions',
				name: 'terms-and-conditions',
				component: () => import('./pages/terms-and-conditions.vue'),
				// meta: {
				// 	hideMainMenuMobile: true,
				// 	hideMainMenuDesktop: true,
				// }
			},
		]
	},
	{
		path: '/monthly-top-user/:id/:urlIdentifier',
		name: 'monthly-top-user-no-lang',
		component: EmptyComponent,
		beforeEnter: async to => {
			// Detect language and redirect
			const store = useStore()
			
			// Since store.currentLanguage may not be available yet on the redirect moment, we need to wait until it is resolved
			await until(() => store.currentLanguage)
			
			// return new redirect destination with the language set
			return {
				name: "monthly-top-user",
				params: {
					id: to.params.id,
					urlIdentifier: to.params.urlIdentifier,
					language: store.currentLanguage,
				}
			}
		}
	},
]

// Setup router
const router = createRouter({
	history: createWebHistory(),
  routes
})

const t = computed(() => useStore().translations)

// This is a helper to act when route is unknown
async function navigateOnUnknownRoute(route){
	// Detect language and redirect
	const store = useStore()
	const userStore = useUserStore()

	// Since store.currentLanguage may not be available yet on the redirect moment, we need to wait until it is resolved
	await until(() => store.currentLanguage)

	// Set language parameter when resolved
	route.params.language = store.currentLanguage

	const newRouteName = userStore.loggedIn ? "dashboard" : "sign-in"

	console.log(`The URL is empty. Throwing to the "${newRouteName}" page`);

	// return new redirect destination with the language set
	return {
		name: newRouteName,
		params: route.params,
		query: route.query // Pass query string, it may containg params like utm_source, utm_medium
	}
}

// Process various routes
async function processNavigationGuards(route){
	return await LanguageRouteGuard(route)
		|| await AuthRouteGuard(route)
		|| await NonAuthRouteGuard(route)
}

async function NonAuthRouteGuard(route){
	const store = useStore()
	const userStore = useUserStore()

	// Wait until loggedIn is resolved
	await until(() => userStore.loggedIn !== undefined)

	// If logged-in user visits the page for non-logged-in users. Direct it to dashboard
	if(userStore.loggedIn && route.meta.requiresNonAuth){
		console.log("The user is connected while trying to access route available for non-authorized users only. Redirecting the dashboard");
		
		return {
			name: 'dashboard',
			params: {
				language: store.currentLanguage
			},
			query: route.query // Pass query string, it may containg params like utm_source, utm_medium
		}
	}
}

// If not authorized user visits the page for logged-in users only redirect it to the sign in page
async function AuthRouteGuard(route){
	const store = useStore()
	const userStore = useUserStore()

	// Wait until loggedIn is resolved
	await until(() => userStore.loggedIn !== undefined)

	if(!userStore.loggedIn && route.matched.some(record => record.meta.requiresAuth)) {
		console.log('User is not connected. Throwing to sign-in page');
		
		return {
			name: 'sign-in',
			params: {
				language: store.currentLanguage
			},
			query: { ...route.query, ...{ redirectTo: encodeURIComponent(route.fullPath) }} // Pass query string, it may containg params like utm_source, utm_medium
		}
	}
}

// Figure out if we have a route and it's not a just a language grouping route like "/" or "/en". If no - redirect to the default one. 
async function LanguageRouteGuard(route){
	if(route.matched.length == 0 ||
		(route.matched.length == 1 && route.matched[0].name == "language")){
		return navigateOnUnknownRoute(route)
	}
}

// Initialize navigation guards
router.beforeResolve(async to => {
	const store = useStore()
	const userStore = useUserStore()

	// Wait until loggedIn is resolved
	await until(() => userStore.loggedIn !== undefined)

	// Since store.currentLanguage may not be available yet on the redirect moment, we need to wait until it is resolved
	await until(() => store.currentLanguage)

	// Load translations if not yet loaded
	if(!t.value){
		await load()
	}

	// Process various routes
	return await processNavigationGuards(to)
})

router.beforeEach((to, from) => {
	const userStore = useUserStore()

	// If navigation happens before user information initialized, it's too early to process guards. Exiting
	if(userStore.loggedIn === undefined){
		return;
	}

	// Catch modal toggling on URL change and don't do anything from the code below.
	if(to.fullPath == from.fullPath || (from.path == to.path && isModalToggling(from.query, to.query))){
		return;
	}

	// Add scroll to top page on route change (expected behaviour)
	scrollTo(document.body)

	// Check if the route we navigate to requires auth
	if(to.matched.some(record => record.meta.requiresAuth)) {
		// this route requires auth, check if the user is logged in
		// if not, redirect to login page.
		if(userStore.loggedIn === false) {
			return {
				name: 'sign-in',
			}
		} else {
			return;
		}
	}
})

router.afterEach(async route => {
	// Wait for translations to load and set document.title to the value set in route.meta.title() key or to t.mainPageTitle fallback
	await until(() => !!t.value)
	
	if(route.meta.title){
		document.title = (route.meta.title as () => string)()
	}else{
		document.title = t.value.mainPageTitle
	}
})

/**
 * Helpers
 */
export function resetQueryStringInRoute(){
	router.replace({ query: {} })
	history.replaceState({}, "", location.pathname)
}

// It is a helper for modals to change URL so "Back" could work
export function setRouteUrlParam(key, value = null){
	if(value){
		// add query param
		const param = {};
		param[key] = value
		
		router.push({
			query: {...router.currentRoute.value.query, ...param}
		})
	}else{
		// remove query param
		var query = { // We need to use spread here to copy the object. Otherwise replace does not work
			...router.currentRoute.value.query
		}
		delete query[key]
		
		router.replace({
			query
		})
	}
}

export { 
	router,
	routes,
	isNavigationFailure,
	NavigationFailureType
}